import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { formatDistanceToNowStrict } from "date-fns"
import {
	IconArrowDownOutline18 as SortIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import {
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	IconButton,
	Tabs,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { ActivityRow } from "./api-activity-block"
import { MetaInfo } from "@/components/meta-info"
import { api } from "@/lib/api"
import { SettingsHeader } from "../../../settings-header"
import { summarizeArgs } from "../../../settings-pages/mcp-activity-json"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import {
	ActivityDetailSheet,
	isFailure,
	statusLabel,
} from "./api-activity-block"

const PAGE_SIZE = 10
const RETENTION_DAYS = 14

type Source = "api" | "mcp"
type Sort = "newest" | "oldest"

const SOURCE_LABELS: Record<Source, string> = { api: "REST", mcp: "MCP" }

export function ApiRequestsPage() {
	const [tab, setTab] = useState<"all" | "failed">("all")
	const [source, setSource] = useState<Source | null>(null)
	const [apiKeyId, setApiKeyId] = useState<string | null>(null)
	const [sort, setSort] = useState<Sort>("newest")
	const [limit, setLimit] = useState(PAGE_SIZE)
	const [keys] = useQuery(queries.apiKeysV2())

	const query = api.apiAnalytics.workspaceApiActivity.useQuery(
		{
			days: RETENTION_DAYS,
			limit,
			failed: tab === "failed",
			source: source ?? undefined,
			apiKeyId: apiKeyId ?? undefined,
			sort,
		},
		{ keepPreviousData: true },
	)
	const rows = query.data ?? []
	const keyName = keys.find((k) => k.id === apiKeyId)?.label

	return (
		<>
			<MetaInfo title="Requests" />
			<SettingsHeader
				title="Requests"
				subtitle="Every call to the public API. MCP calls carry their tool name and arguments."
			/>

			<div className="mt-6 flex flex-wrap items-center justify-between gap-2">
				<Tabs value={tab} onValueChange={(v) => setTab(v as "all" | "failed")}>
					<TabsList>
						<TabsTrigger value="all">All</TabsTrigger>
						<TabsTrigger value="failed">Failed</TabsTrigger>
					</TabsList>
				</Tabs>
				<div className="flex items-center gap-2">
					<DropdownMenu
						trigger={<Button variant="outline">Filter</Button>}
						contentProps={{ align: "end" }}
					>
						<DropdownMenuSub>
							<DropdownMenuSubTrigger>Type</DropdownMenuSubTrigger>
							<DropdownMenuSubContent>
								{(Object.keys(SOURCE_LABELS) as Source[]).map((s) => (
									<DropdownMenuItem key={s} onSelect={() => setSource(s)}>
										{SOURCE_LABELS[s]}
									</DropdownMenuItem>
								))}
							</DropdownMenuSubContent>
						</DropdownMenuSub>
						{keys.length > 0 && (
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>API key</DropdownMenuSubTrigger>
								<DropdownMenuSubContent>
									{keys.map((k) => (
										<DropdownMenuItem
											key={k.id}
											onSelect={() => setApiKeyId(k.id)}
										>
											{k.label}
										</DropdownMenuItem>
									))}
								</DropdownMenuSubContent>
							</DropdownMenuSub>
						)}
					</DropdownMenu>
				</div>
			</div>

			{(source || apiKeyId) && (
				<div className="mt-3 flex flex-wrap gap-1.5">
					{source && (
						<FilterChip
							label={`Type is ${SOURCE_LABELS[source]}`}
							onRemove={() => setSource(null)}
						/>
					)}
					{apiKeyId && (
						<FilterChip
							label={`Key is ${keyName ?? "deleted key"}`}
							onRemove={() => setApiKeyId(null)}
						/>
					)}
				</div>
			)}

			<SettingsContainer className="mb-0 mt-4 px-0 pb-0">
				{rows.length === 0 ? (
					<div className="px-4 py-10 text-center text-sm text-tertiary">
						{query.isLoading
							? "Loading…"
							: tab === "failed"
								? "No failed requests in the last 14 days"
								: "No requests in the last 14 days"}
					</div>
				) : (
					<div role="table" aria-label="Requests" className="flex flex-col">
						<div
							role="row"
							className={cn(
								GRID,
								"border-b border-gray-200 px-4 py-2 text-xs text-tertiary dark:border-gray-700/80",
							)}
						>
							<button
								type="button"
								role="columnheader"
								aria-sort={sort === "newest" ? "descending" : "ascending"}
								onClick={() => setSort(sort === "newest" ? "oldest" : "newest")}
								className="flex items-center gap-1 text-left hover:text-secondary"
							>
								Time
								<SortIcon
									className={cn(
										"h-3 w-3 flex-none",
										sort === "oldest" && "rotate-180",
									)}
								/>
							</button>
							<span role="columnheader">Type</span>
							<span role="columnheader">Call</span>
							<span role="columnheader">Duration</span>
							<span role="columnheader">Status</span>
						</div>
						{rows.map((row) => (
							<RequestRow key={row.id} row={row} />
						))}
					</div>
				)}
			</SettingsContainer>

			{rows.length >= limit && limit < 200 && (
				<div className="mt-3 flex justify-center">
					<Button
						variant="ghost"
						isLoading={query.isFetching}
						onClick={() => setLimit((l) => Math.min(l + PAGE_SIZE, 200))}
					>
						Load more
					</Button>
				</div>
			)}
		</>
	)
}

function FilterChip({
	label,
	onRemove,
}: {
	label: string
	onRemove: () => void
}) {
	return (
		<Badge variant="dark-gray" className="gap-1 pr-0.5">
			{label}
			<IconButton
				Icon={XIcon}
				variant="ghost"
				size="sm"
				aria-label={`Remove filter: ${label}`}
				onClick={onRemove}
			/>
		</Badge>
	)
}

const GRID =
	"grid grid-cols-[96px_52px_minmax(0,1fr)_64px_96px] items-center gap-3"

function RequestRow({ row }: { row: ActivityRow }) {
	const [open, setOpen] = useState(false)
	const failed = isFailure(row)
	return (
		<>
			<button
				type="button"
				// eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- grid row that opens the request detail
				role="row"
				onClick={() => setOpen(true)}
				className={cn(
					GRID,
					"w-full border-b border-gray-200 px-4 py-2.5 text-left text-sm last:rounded-b-lg last:border-b-0 hover:bg-gray-50 dark:border-gray-700/80 dark:hover:bg-gray-800/50",
				)}
			>
				<span className="truncate text-xs text-tertiary">
					{formatDistanceToNowStrict(new Date(row.createdAt), {
						addSuffix: true,
					})}
				</span>
				<span className="text-xs text-secondary">
					{row.source === "mcp" ? "MCP" : "REST"}
				</span>
				<span className="flex min-w-0 items-baseline gap-2 font-mono">
					<span className="max-w-full flex-none truncate text-sm text-primary">
						{row.toolName ?? `${row.method} ${row.path}`}
					</span>
					{row.source === "mcp" && (
						<span className="min-w-0 truncate text-xs text-tertiary">
							{summarizeArgs(row.args, row.argKeys)}
						</span>
					)}
				</span>
				<span className="text-xs text-tertiary">{row.durationMs} ms</span>
				<Badge
					variant={failed ? "red" : "green"}
					size="sm"
					className="w-fit whitespace-nowrap"
				>
					{statusLabel(row)}
				</Badge>
			</button>
			<ActivityDetailSheet row={row} open={open} onOpenChange={setOpen} />
		</>
	)
}
